import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Newspaper, 
  Briefcase, 
  Users, 
  MapPin, 
  Settings,
  LogOut,
  FileText,
  Image,
  HelpCircle,
  Award,
  MessageSquare
} from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from '../hooks/use-toast';
import NewsManagement from './admin/NewsManagement';
import CompanyInfoManagement from './admin/CompanyInfoManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    quotes: 0,
    news: 0,
    services: 0,
  });

  useEffect(() => {
    verifyAuth();
    loadStats();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      await apiService.verifyToken();
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const loadStats = async () => {
    try {
      const [quotesRes, newsRes, servicesRes] = await Promise.all([
        apiService.getQuotes(),
        apiService.getNews(false),
        apiService.getServices(),
      ]);
      
      setStats({
        quotes: quotesRes.data.length,
        news: newsRes.data.length,
        services: servicesRes.data.length,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast({
      title: 'Çıkış yapıldı',
      description: 'Başarıyla çıkış yaptınız.',
    });
    navigate('/admin');
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'quotes', label: 'Teklif Talepleri', icon: MessageSquare },
    { id: 'hero', label: 'Ana Slaytlar', icon: Image },
    { id: 'services', label: 'Servisler', icon: Briefcase },
    { id: 'company', label: 'Şirket Bilgileri', icon: FileText },
    { id: 'news', label: 'Haberler', icon: Newspaper },
    { id: 'offices', label: 'Ofisler', icon: MapPin },
    { id: 'team', label: 'Ekip', icon: Users },
    { id: 'faq', label: 'SSS', icon: HelpCircle },
    { id: 'references', label: 'Referanslar', icon: Award },
    { id: 'gallery', label: 'Galeri', icon: Image },
    { id: 'settings', label: 'Ayarlar', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DF6A6A] mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#36344D] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_serene-roentgen-1/artifacts/4ynwgged_LOGO%20PNG%20-COLOR-02.png"
                alt="Movent Logistics"
                className="h-12"
              />
              <h1 className="text-xl font-bold">Yönetim Paneli</h1>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-[#DF6A6A] rounded-md hover:bg-[#c55555] transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4">
              <nav className="space-y-2">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? 'bg-[#DF6A6A] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-6">
              {activeTab === 'dashboard' && (
                <div>
                  <h2 className="text-2xl font-bold text-[#36344D] mb-6">Dashboard</h2>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Teklif Talepleri</p>
                          <p className="text-3xl font-bold mt-2">{stats.quotes}</p>
                        </div>
                        <MessageSquare className="w-12 h-12 opacity-80" />
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Haberler</p>
                          <p className="text-3xl font-bold mt-2">{stats.news}</p>
                        </div>
                        <Newspaper className="w-12 h-12 opacity-80" />
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Servisler</p>
                          <p className="text-3xl font-bold mt-2">{stats.services}</p>
                        </div>
                        <Briefcase className="w-12 h-12 opacity-80" />
                      </div>
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#36344D] mb-4">Hızlı Erişim</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {menuItems.slice(1, 5).map((item) => (
                        <button
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#DF6A6A] hover:bg-[#DF6A6A]/5 transition-all"
                        >
                          <item.icon className="w-8 h-8 text-[#DF6A6A] mx-auto mb-2" />
                          <p className="text-sm font-medium text-gray-700">{item.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab !== 'dashboard' && activeTab !== 'news' && activeTab !== 'company' && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🚧</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {menuItems.find(item => item.id === activeTab)?.label}
                  </h3>
                  <p className="text-gray-600">
                    Bu bölüm şu anda geliştirilme aşamasındadır.
                  </p>
                  <p className="text-sm text-gray-500 mt-4">
                    Tüm admin panel özellikleri yakında eklenecek!
                  </p>
                </div>
              )}
              
              {activeTab === 'news' && <NewsManagement />}
              {activeTab === 'company' && <CompanyInfoManagement />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
